import { useEffect, useState } from 'react';
import { getMe, getToken, logout as apiLogout, type AuthUser } from './api';
import { useIdleLogout } from './useIdleLogout';
import FragenAdmin from './pages/Fragen';
import Login from './pages/Login';
import Praxen from './pages/Praxen';
import RezeptEditor from './pages/RezeptEditor';
import Statistik from './pages/Statistik';
import Users from './pages/Users';

type Page = 'statistik' | 'fragen' | 'praxen' | 'rezepte' | 'users';

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [page, setPage] = useState<Page>('statistik');
  const [idleWarning, setIdleWarning] = useState(false);

  useIdleLogout({
    enabled: Boolean(user),
    onWarning: () => setIdleWarning(true),
    onIdle: async () => {
      await apiLogout();
      setUser(null);
      setIdleWarning(false);
      alert('Aus Sicherheitsgründen wurden Sie nach 15 Minuten Inaktivität abgemeldet.');
    },
  });

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setChecking(false);
      return;
    }
    getMe()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="login-overlay">
        <div className="login-box">Wird geladen…</div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={() => getMe().then((r) => setUser(r.user))} />;
  }

  const onLogout = async () => {
    await apiLogout();
    setUser(null);
  };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-logo">B</div>
          <div>
            <div className="brand-name">Bertsy</div>
            <div className="brand-sub">Admin-Panel</div>
          </div>
        </div>

        <nav className="nav">
          <div className="nav-section">Übersicht</div>
          <button className={page === 'statistik' ? 'active' : ''} onClick={() => setPage('statistik')}>
            <span className="nav-icon">📊</span>
            Statistik
          </button>
          <button className={page === 'fragen' ? 'active' : ''} onClick={() => setPage('fragen')}>
            <span className="nav-icon">💬</span>
            Patienten-Fragen
          </button>

          <div className="nav-section">Inhalte</div>
          <button className={page === 'praxen' ? 'active' : ''} onClick={() => setPage('praxen')}>
            <span className="nav-icon">🏥</span>
            Praxen
          </button>
          <button className={page === 'rezepte' ? 'active' : ''} onClick={() => setPage('rezepte')}>
            <span className="nav-icon">📋</span>
            Rezepte bearbeiten
          </button>

          <div className="nav-section">Verwaltung</div>
          <button className={page === 'users' ? 'active' : ''} onClick={() => setPage('users')}>
            <span className="nav-icon">👤</span>
            Admin-Nutzer
          </button>
        </nav>

        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, color: '#5F6B68', padding: '0 8px' }}>angemeldet als</div>
          <div style={{ fontSize: 13, fontWeight: 600, padding: '0 8px', wordBreak: 'break-all' }}>{user.email}</div>
          <button className="logout-btn" onClick={onLogout}>Abmelden</button>
        </div>
      </aside>

      <main className="content">
        {page === 'statistik' && <Statistik />}
        {page === 'fragen' && <FragenAdmin />}
        {page === 'praxen' && <Praxen />}
        {page === 'rezepte' && <RezeptEditor />}
        {page === 'users' && <Users />}
      </main>

      {idleWarning && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          background: '#FFEBEE', border: '1px solid #D9534F', borderLeft: '4px solid #D9534F',
          padding: '12px 16px', borderRadius: 8, maxWidth: 360,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        }}>
          <strong style={{ color: '#C62828' }}>⚠ Bald automatischer Logout</strong>
          <p style={{ fontSize: 13, margin: '4px 0 0' }}>
            Aus DSGVO-Gründen werden Sie in 1 Minute abgemeldet, wenn keine Aktivität stattfindet.
          </p>
        </div>
      )}
    </div>
  );
}
